import BulkCsvImport from './BulkCsvImport';
import { csvNumber } from '../utils/bulkCsv';
import { getSupplierPurchaseHistory } from '../utils/supplierPurchaseHistory';
import React, { useMemo, useState } from 'react';
import './CreatePurchaseOrderSection.css';
import { useDispatch, useSelector } from 'react-redux';
import { openingStockDraftKey, readOpeningStockDraft, saveOpeningStockDraft } from '../utils/openingStockDraft';

import {
  createPurchaseOrdersBySupplier,
  fetchPurchaseOrders,
  fetchSupplierProductBarcodes,
} from '../features/inventory/stockManagerInventorySlice';

const searchText = (value) => String(value || '').toLowerCase();

const getBrandName = (item) =>
  item?.brand_name_english ||
  item?.brand_name_englishh ||
  item?.brand_name_telugu ||
  '';

const SuggestInput = ({
  label,
  value,
  onChange,
  placeholder,
  suggestions = [],
  onSelect,
  renderItem,
  readOnly = false,
  dropdownClassName = '',
}) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      {label && (
        <label className="mb-1 block text-xs font-semibold text-gray-700">
          {label}
        </label>
      )}

      <input
        value={value}
        readOnly={readOnly}
        onFocus={() => !readOnly && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (readOnly || e.key !== 'Enter') return;

          // Barcode scanners normally append Enter. Keep that Enter inside the
          // lookup instead of letting it submit the surrounding purchase form.
          e.preventDefault();
          e.stopPropagation();

          if (suggestions.length > 0) {
            onSelect(suggestions[0]);
            setOpen(false);
          }
        }}
        onChange={(e) => {
          if (readOnly) return;
          onChange(e.target.value);
          setOpen(true);
        }}
        placeholder={placeholder}
        className={`h-11 w-full rounded-lg border px-3 py-2 text-sm ${
          readOnly ? 'cursor-not-allowed bg-gray-100' : 'bg-white'
        }`}
      />

      {!readOnly && open && suggestions.length > 0 && (
        <div
          className={`absolute z-50 mt-1 max-h-80 overflow-auto rounded-xl border bg-white shadow-xl ${
            dropdownClassName || 'w-full'
          }`}
        >
          {suggestions.map((item, index) => (
            <button
              key={`${item.id || index}-${item.mk_barcode || item.barcode || ''}`}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(item);
                setOpen(false);
              }}
              className="block w-full border-b px-3 py-3 text-left hover:bg-yellow-50"
            >
              {renderItem(item)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ProductSuggestionItem = ({ product }) => {
  const productName =
    product.product_name_eng ||
    product.product_name_tel ||
    product.product_code ||
    '-';

  const brandName = getBrandName(product) || '-';
  const categoryName =
    product.category_name_english || product.category_name_telugu || '-';

  const unitName = product.unit_short_code || product.unit_name || '';
  const quantity = Number(product.quantity || product.qty || 0);

  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-bold text-gray-900">
          {productName}
        </div>

        <div className="mt-1 text-xs text-gray-600">
          <span className="font-semibold text-gray-800">MK Barcode:</span>{' '}
          {product.mk_barcode || '-'}
        </div>

        <div className="text-xs text-gray-600">
          <span className="font-semibold text-gray-800">Vendor Barcode:</span>{' '}
          {product.barcode || '-'}
        </div>

        <div className="text-xs text-gray-600">
          <span className="font-semibold text-gray-800">Brand:</span>{' '}
          {brandName}
        </div>

        <div className="text-xs text-gray-600">
          <span className="font-semibold text-gray-800">Category:</span>{' '}
          {categoryName}
        </div>
        {product.supplier_suggested && (
          <div className="mt-1 inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-bold text-blue-700">
            Supplier product
            {product.last_purchase_price
              ? ` | Last â‚¹${Number(product.last_purchase_price || 0).toFixed(2)}`
              : ''}
          </div>
        )}
      </div>

      <div className="shrink-0 rounded-lg bg-green-50 px-3 py-2 text-right">
        <div className="text-[11px] font-semibold text-green-700">Qty</div>
        <div className="text-base font-bold text-green-900">
          {quantity.toFixed(2)}
        </div>
        <div className="text-[11px] font-bold text-green-700">
          {unitName || '-'}
        </div>
      </div>
    </div>
  );
};

const CreatePurchaseOrderSection = ({
  initialWarehouseId = '',
  openingStock = false,
  productBarcodes = [],
  supplierProductBarcodes = [],
  purchaseOrders = [],
  suppliers = [],
  warehouses = [],
}) => {
  const dispatch = useDispatch();
  const account = useSelector(state => state.posUser?.userInfo);
  const draftAccount = account?._id || account?.id || account?.email || account?.username;
  const openingDraftKey = openingStockDraftKey(draftAccount, initialWarehouseId);
  const [openingDraftSaved, setOpeningDraftSaved] = useState(false);

  const [warehouseId, setWarehouseId] = useState(initialWarehouseId);
  const [warehouseSearch, setWarehouseSearch] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [remarks, setRemarks] = useState(openingStock ? 'OPENING STOCK — Physical warehouse verification. Existing goods counted in warehouse; not a new supplier delivery.' : '');

  const [supplierId, setSupplierId] = useState('');
  const [supplierSearch, setSupplierSearch] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const [productSearch, setProductSearch] = useState('');
  const [selectedBarcodeRow, setSelectedBarcodeRow] = useState(null);

  const [noOfUnits, setNoOfUnits] = useState(1);
  const [expectedUnitPrice, setExpectedUnitPrice] = useState(0);

  const [items, setItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (!openingStock) return;
    const saved = readOpeningStockDraft(openingDraftKey);
    if (saved) {
      setItems(saved.items || []);
      setRemarks(saved.remarks || 'OPENING STOCK — Physical warehouse verification');
      setExpectedDate(saved.expectedDate || '');
      setOpeningDraftSaved(true);
    }
  }, [openingStock, openingDraftKey]);

  React.useEffect(() => {
    if (!initialWarehouseId) return;
    const warehouse = warehouses.find(row => String(row.id) === String(initialWarehouseId));
    if (warehouse) {
      setWarehouseId(String(warehouse.id));
      setWarehouseSearch(warehouse.warehouse_name || warehouse.warehouse_code || '');
    }
  }, [initialWarehouseId, warehouses]);

  const purchaseHistory = useMemo(
    () => getSupplierPurchaseHistory(purchaseOrders, supplierId, productBarcodes),
    [purchaseOrders, supplierId, productBarcodes]
  );

  const warehouseSuggestions = useMemo(() => {
    if (!warehouseSearch.trim()) return [];
    const value = searchText(warehouseSearch);

    return (warehouses || [])
      .filter(
        (w) =>
          searchText(w.warehouse_name).includes(value) ||
          searchText(w.warehouse_code).includes(value) ||
          searchText(w.address).includes(value)
      )
      .slice(0, 10);
  }, [warehouses, warehouseSearch]);

  const supplierSuggestions = useMemo(() => {
    if (!supplierSearch.trim()) return [];
    const value = searchText(supplierSearch);

    return (suppliers || [])
      .filter((s) => {
        const isSupplier =
          searchText(s.stakeholder_type) === 'supplier' || !s.stakeholder_type;

        return (
          isSupplier &&
          (searchText(s.stakeholder_name).includes(value) ||
            searchText(s.stackholder_code).includes(value) ||
            searchText(s.phone).includes(value))
        );
      })
      .slice(0, 10);
  }, [suppliers, supplierSearch]);

  const supplierMappedProducts = useMemo(() => {
    if (!supplierId) return [];

    return (supplierProductBarcodes || [])
      .filter((item) => String(item.supplier_id) === String(supplierId))
      .map((item) => ({
        ...item,
        id: item.product_barcode_id,
        supplier_product_id: item.id,
        supplier_suggested: true,
      }));
  }, [supplierId, supplierProductBarcodes]);

  const productSuggestions = useMemo(() => {
    const value = searchText(productSearch);
    const knownProductBarcodeIds = new Set(
      supplierMappedProducts.map((item) => String(item.product_barcode_id || item.id))
    );

    const matchesSearch = (p) => {
      const brandName = getBrandName(p);

      if (!value) return true;
      return (
        searchText(p.product_name_eng).includes(value) ||
        searchText(p.product_name_tel).includes(value) ||
        searchText(p.product_code).includes(value) ||
        searchText(p.mk_barcode).includes(value) ||
        searchText(p.barcode).includes(value) ||
        searchText(brandName).includes(value) ||
        searchText(p.category_name_english).includes(value) ||
        searchText(p.category_name_telugu).includes(value) ||
        searchText(p.unit_name).includes(value) ||
        searchText(p.unit_short_code).includes(value)
      );
    };

    const supplierSuggestions = supplierMappedProducts
      .filter(matchesSearch)
      .slice(0, 25);

    if (supplierSuggestions.length && !value) {
      return supplierSuggestions;
    }

    const catalogSuggestions = (productBarcodes || [])
      .filter((p) => value && matchesSearch(p))
      .filter((p) => !knownProductBarcodeIds.has(String(p.id)))
      .slice(0, 25);

    return [...supplierSuggestions, ...catalogSuggestions].slice(0, 25);
  }, [productBarcodes, productSearch, supplierMappedProducts]);

  const selectWarehouse = (warehouse) => {
    setWarehouseId(warehouse.id);
    setWarehouseSearch(
      warehouse.warehouse_name || warehouse.warehouse_code || ''
    );
  };

  const selectSupplier = (supplier) => {
    setSupplierId(supplier.id);
    setSelectedSupplier(supplier);
    setSupplierSearch(
      supplier.stakeholder_name || supplier.stackholder_code || ''
    );
    setProductSearch('');
    setSelectedBarcodeRow(null);
  };

  const clearSelectedProduct = () => {
    setSelectedBarcodeRow(null);
  };

  const selectProductBarcode = (barcodeRow) => {
    setSelectedBarcodeRow(barcodeRow);
    if (barcodeRow.last_purchase_price !== undefined) {
      setExpectedUnitPrice(Number(barcodeRow.last_purchase_price || 0));
    }

    setProductSearch(
      barcodeRow.product_name_eng ||
        barcodeRow.product_name_tel ||
        barcodeRow.product_code ||
        barcodeRow.mk_barcode ||
        barcodeRow.barcode ||
        ''
    );
  };

  const clearItemInputs = () => {
    setSupplierId('');
    setSupplierSearch('');
    setSelectedSupplier(null);

    setProductSearch('');
    setSelectedBarcodeRow(null);

    setNoOfUnits(1);
    setExpectedUnitPrice(0);
  };

  const addItem = () => {
    if (!supplierId || !selectedBarcodeRow) {
      alert('Supplier and product barcode are required');
      return;
    }

    if (!selectedBarcodeRow.product_id) {
      alert('Selected barcode row does not have product_id');
      return;
    }

    if (!selectedBarcodeRow.brand_id) {
      alert('Selected barcode row does not have brand_id');
      return;
    }

    if (!selectedBarcodeRow.category_id) {
      alert('Selected barcode row does not have category_id');
      return;
    }

    if (!selectedBarcodeRow.unit_id) {
      alert('Selected barcode row does not have unit_id');
      return;
    }

    const brandName = getBrandName(selectedBarcodeRow);
    const categoryName =
      selectedBarcodeRow.category_name_english ||
      selectedBarcodeRow.category_name_telugu ||
      '';

    const unitName =
      selectedBarcodeRow.unit_short_code || selectedBarcodeRow.unit_name || '';

    const productName =
      selectedBarcodeRow.product_name_eng ||
      selectedBarcodeRow.product_name_tel ||
      selectedBarcodeRow.product_code ||
      '';

    setItems((prev) => [
      ...prev,
      {
        supplier_id: Number(supplierId),
        supplier_name:
          selectedSupplier?.stakeholder_name ||
          selectedSupplier?.stackholder_code ||
          '',

        product_barcode_id: selectedBarcodeRow.id
          ? Number(selectedBarcodeRow.id)
          : null,

        product_id: Number(selectedBarcodeRow.product_id),
        product_name: productName,
        product_code: selectedBarcodeRow.product_code || '',

        mk_barcode: selectedBarcodeRow.mk_barcode || '',
        barcode: selectedBarcodeRow.barcode || '',

        category_id: Number(selectedBarcodeRow.category_id),
        category_name: categoryName,

        brand_id: Number(selectedBarcodeRow.brand_id),
        brand_name: brandName,

        unit_id: Number(selectedBarcodeRow.unit_id),
        unit_name: unitName,

        qty: Number(selectedBarcodeRow.quantity || selectedBarcodeRow.qty || 1),
        no_of_units: Number(noOfUnits || 1),
        expected_unit_price: Number(expectedUnitPrice || 0),
        actual_unit_price: null,
      },
    ]);

    clearItemInputs();
  };

  const updateRow = (index, field, value) => {
    setItems((prev) => {
      const copy = [...prev];
      copy[index] = {
        ...copy[index],
        [field]: value,
      };
      return copy;
    });
  };

  const removeItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const groupedBySupplier = useMemo(() => {
    const map = new Map();

    for (const item of items) {
      const key = String(item.supplier_id);

      if (!map.has(key)) {
        map.set(key, {
          supplier_id: item.supplier_id,
          supplier_name: item.supplier_name,
          items: [],
        });
      }

      map.get(key).items.push({
        product_id: Number(item.product_id),
        category_id: Number(item.category_id),
        brand_id: Number(item.brand_id),
        unit_id: Number(item.unit_id),

        product_barcode_id: item.product_barcode_id,

        qty: Number(item.qty),
        no_of_units: Number(item.no_of_units || 1),
        expected_unit_price: Number(item.expected_unit_price || 0),
        actual_unit_price: null,

        product_name: item.product_name,
        product_code: item.product_code,
        category_name: item.category_name,
        brand_name: item.brand_name,
        unit_name: item.unit_name,
        mk_barcode: item.mk_barcode,
        barcode: item.barcode,
      });
    }

    return Array.from(map.values());
  }, [items]);

  const grandTotal = items.reduce(
    (sum, item) =>
      sum +
      Number(item.no_of_units || 1) * Number(item.expected_unit_price || 0),
    0
  );

  const submitHandler = async (e, publishOpening = false) => {
    e?.preventDefault();

    if (submitting) return;

    if (!warehouseId) {
      alert('Warehouse is required');
      return;
    }

    if (items.length === 0) {
      alert('Please add at least one item');
      return;
    }

    try {
      setSubmitting(true);

      if (openingStock && !publishOpening) {
        if (String(warehouseId) !== String(initialWarehouseId)) throw new Error('Opening stock must use the selected validation warehouse.');
        saveOpeningStockDraft(openingDraftKey, { warehouseId, items, remarks, expectedDate, savedAt: new Date().toISOString() });
        setOpeningDraftSaved(true);
        alert('Opening-stock draft saved on this browser. No purchase or stock has been created. Publish opening-stock draft when ready.');
        return;
      }

      if (openingStock) {
        // Persist progress after every supplier to avoid duplicating completed groups on retry.
        const saved = readOpeningStockDraft(openingDraftKey);
        if (!saved?.items?.length) throw new Error('Save the opening-stock draft before publishing.');
        const groups = Object.values(saved.items.reduce((result, item) => {
          const key = String(item.supplier_id);
          (result[key] ||= { supplier_id: item.supplier_id, supplier_name: item.supplier_name, items: [] }).items.push(item);
          return result;
        }, {}));
        let remaining = saved.items;
        for (const group of groups) {
          await dispatch(createPurchaseOrdersBySupplier({ warehouse_id: Number(saved.warehouseId), expected_date: saved.expectedDate || null,
            remarks: saved.remarks || null, openingStock: true, supplierGroups: [group] })).unwrap();
          remaining = remaining.filter(item => String(item.supplier_id) !== String(group.supplier_id));
          saveOpeningStockDraft(openingDraftKey, { ...saved, items: remaining });
          setItems(remaining);
        }
        localStorage.removeItem(openingDraftKey);
        setOpeningDraftSaved(false);
        await dispatch(fetchPurchaseOrders()).unwrap();
        alert('Opening-stock purchases published as Received. Verify them to add their stock to inventory.');
        return;
      }

      await dispatch(
        createPurchaseOrdersBySupplier({
          warehouse_id: Number(warehouseId),
          expected_date: expectedDate || null,
          remarks: remarks || null,
          supplierGroups: groupedBySupplier,
        })
      ).unwrap();

      await dispatch(fetchPurchaseOrders()).unwrap();
      dispatch(fetchSupplierProductBarcodes());

      setWarehouseId('');
      setWarehouseSearch('');
      setExpectedDate('');
      setRemarks('');
      setItems([]);
      alert('Purchase order created successfully.');
    } catch (error) {
      alert(error || 'Failed to create purchase order.');
    } finally {
      setSubmitting(false);
    }
  };

  const importPurchaseCsv = (rows) => {
    if (!warehouseId) throw new Error('Select the receiving warehouse first.');
    const imported = rows.map((row, index) => {
      try {
        const supplier = suppliers.find(s => String(s.id) === row.supplier_id);
        if (!supplier || (supplier.stakeholder_type && searchText(supplier.stakeholder_type) !== 'supplier')) throw new Error('Unknown supplier_id or stakeholder is not a supplier.');
        const product = productBarcodes.find(p => String(p.id) === row.product_barcode_id);
        if (!product || !product.product_id || !product.brand_id || !product.category_id || !product.unit_id) throw new Error('Unknown or incomplete product_barcode_id.');
        return {
          supplier_id:Number(supplier.id), supplier_name:supplier.stakeholder_name || supplier.stackholder_code,
          product_barcode_id:Number(product.id), product_id:Number(product.product_id),
          product_name:product.product_name_eng || product.product_name_tel || product.product_code,
          product_code:product.product_code, mk_barcode:product.mk_barcode, barcode:product.barcode,
          brand_id:Number(product.brand_id), category_id:Number(product.category_id), unit_id:Number(product.unit_id),
          brand_name:getBrandName(product), category_name:product.category_name_english || product.category_name_telugu,
          unit_name:product.unit_short_code || product.unit_name, qty:Number(product.quantity || product.qty || 1),
          no_of_units:csvNumber(row.no_of_units,'no_of_units'), expected_unit_price:csvNumber(row.expected_unit_price,'expected_unit_price',true),
        };
      } catch (error) { throw new Error(`Row ${index + 2}: ${error.message}`); }
    });
    setItems(prev => [...prev, ...imported]);
  };

  const addHistoryItem = (row) => {
    const item = row.purchaseItem;
    if (['product_id', 'category_id', 'brand_id', 'unit_id'].some(field => !item[field])) {
      alert('This previous purchase is missing product details. Select the product from the catalogue to add it.');
      return;
    }
    setItems(previous => [...previous, {
      ...item,
      supplier_name: selectedSupplier?.stakeholder_name || selectedSupplier?.stackholder_code || '',
    }]);
  };

  const selectedProductName =
    selectedBarcodeRow?.product_name_eng ||
    selectedBarcodeRow?.product_name_tel ||
    selectedBarcodeRow?.product_code ||
    '';

  const selectedBrandName = getBrandName(selectedBarcodeRow);
  const selectedCategoryName =
    selectedBarcodeRow?.category_name_english ||
    selectedBarcodeRow?.category_name_telugu ||
    '';

  const selectedUnitName =
    selectedBarcodeRow?.unit_short_code || selectedBarcodeRow?.unit_name || '';

  const selectedQty = Number(
    selectedBarcodeRow?.quantity || selectedBarcodeRow?.qty || 0
  );

  return (
    <section className="rounded-xl border bg-white p-4 shadow-sm">
      <h2 className="mb-4 text-lg font-bold">Create Purchase Orders</h2>
      <BulkCsvImport name="purchase" headers={['supplier_id','product_barcode_id','no_of_units','expected_unit_price']} onImport={importPurchaseCsv} disabled={submitting}
        editOptions={{
          supplier_id: suppliers.map(s => ({ value: String(s.id), label: s.stakeholder_name || s.stackholder_code || String(s.id) })),
          product_barcode_id: productBarcodes.map(p => ({ value: String(p.id), label: `${p.product_name_eng || p.product_name_tel || p.product_code} · ${p.quantity || p.qty || 1} ${p.unit_short_code || p.unit_name || ''} · ${p.mk_barcode || ''}` }))
        }}
        previewColumns={[
          { key: 'supplier', label: 'Supplier', render: row => {
            const supplier = suppliers.find(s => String(s.id) === row.supplier_id);
            return supplier ? supplier.stakeholder_name || supplier.stackholder_code : `Unknown supplier (ID: ${row.supplier_id})`;
          } },
          { key: 'product', label: 'Product details', render: row => {
            const product = productBarcodes.find(p => String(p.id) === row.product_barcode_id);
            if (!product) return `Unknown product (ID: ${row.product_barcode_id})`;
            return <div>
              <div className="font-medium">{product.product_name_eng || product.product_name_tel || product.product_code}</div>
              <div className="text-xs text-gray-600">{[
                getBrandName(product),
                `${product.quantity || product.qty || 1} ${product.unit_short_code || product.unit_name || ''}`,
                product.mk_barcode ? `MK barcode: ${product.mk_barcode}` : ''
              ].filter(Boolean).join(' · ')}</div>
            </div>;
          } },
          { key: 'units', label: 'No. of units', render: row => row.no_of_units },
          { key: 'price', label: 'Expected price / pack', render: row => row.expected_unit_price }
        ]}
        help="Select a receiving warehouse and expected date below. One row per supplier/product pack; multiple suppliers create separate purchase orders. Prices are per pack. Repeated products remain separate lines."
        referenceHeaders={['supplier_id','supplier_name','product_barcode_id','product_name','pack_quantity','unit','mk_barcode']}
        referenceRows={supplierProductBarcodes.map(p => ({ ...p, supplier_name: suppliers.find(s => String(s.id) === String(p.supplier_id))?.stakeholder_name, product_name:p.product_name_eng, pack_quantity:p.quantity, unit:p.unit_short_code }))} />

      <form onSubmit={submitHandler} className="space-y-5">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <SuggestInput
            label="Warehouse"
            value={warehouseSearch}
            onChange={(value) => {
              setWarehouseSearch(value);
              setWarehouseId('');
            }}
            placeholder="Search warehouse"
            suggestions={warehouseSuggestions}
            onSelect={selectWarehouse}
            renderItem={(w) => (
              <>
                <div className="font-medium">{w.warehouse_name}</div>
                <div className="text-xs text-gray-500">
                  {w.warehouse_code} | {w.address || '-'}
                </div>
              </>
            )}
          />

          <div>
            <label className="mb-1 block text-xs font-semibold">
              Expected Date
            </label>
            <input
              type="date"
              value={expectedDate}
              onChange={(e) => setExpectedDate(e.target.value)}
              className="h-11 w-full rounded-lg border px-3 py-2"
            />
          </div>

          <div className="md:col-span-2">
            <label className="mb-1 block text-xs font-semibold">Remarks</label>
            <input
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Purchase remarks"
              className="h-11 w-full rounded-lg border px-3 py-2"
            />
          </div>
        </div>

        <section className="rounded-xl border bg-gray-50 p-4">
          <h3 className="mb-3 font-semibold">Add Item</h3>

          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12 md:col-span-2">
              <SuggestInput
                label="Supplier"
                value={supplierSearch}
                onChange={(value) => {
                  setSupplierSearch(value);
                  setSupplierId('');
                  setSelectedSupplier(null);
                }}
                placeholder="Search supplier"
                suggestions={supplierSuggestions}
                onSelect={selectSupplier}
                renderItem={(s) => (
                  <>
                    <div className="font-medium">{s.stakeholder_name}</div>
                    <div className="text-xs text-gray-500">
                      {s.stackholder_code} | {s.phone || '-'}
                    </div>
                  </>
                )}
              />
            </div>

            <div className="col-span-12 md:col-span-4">
              <SuggestInput
                label="Product / Barcode"
                value={productSearch}
                onChange={(value) => {
                  setProductSearch(value);
                  clearSelectedProduct();
                }}
                placeholder="Search product / MK barcode / vendor barcode"
                suggestions={productSuggestions}
                onSelect={selectProductBarcode}
                dropdownClassName="w-full min-w-[560px]"
                renderItem={(p) => <ProductSuggestionItem product={p} />}
              />
            </div>

            <div className="col-span-6 md:col-span-2">
              <label className="mb-1 block text-xs font-semibold">
                Category
              </label>
              <input
                value={selectedCategoryName}
                readOnly
                placeholder="Auto"
                className="h-11 w-full cursor-not-allowed rounded-lg border bg-gray-100 px-3 py-2 text-sm"
              />
            </div>

            <div className="col-span-6 md:col-span-2">
              <label className="mb-1 block text-xs font-semibold">Brand</label>
              <input
                value={selectedBrandName}
                readOnly
                placeholder="Auto"
                className="h-11 w-full cursor-not-allowed rounded-lg border bg-gray-100 px-3 py-2 text-sm"
              />
            </div>

            <div className="col-span-6 md:col-span-1">
              <label className="mb-1 block text-xs font-semibold">Unit</label>
              <input
                value={selectedUnitName}
                readOnly
                placeholder="Auto"
                className="h-11 w-full cursor-not-allowed rounded-lg border bg-gray-100 px-3 py-2 text-sm"
              />
            </div>

            <div className="col-span-6 md:col-span-1">
              <label className="mb-1 block text-xs font-semibold">Qty</label>
              <input
                type="number"
                value={selectedQty || ''}
                readOnly
                placeholder="Auto"
                className="h-11 w-full cursor-not-allowed rounded-lg border bg-gray-100 px-2 py-2 text-right text-sm"
              />
            </div>

            {selectedBarcodeRow && (
              <div className="col-span-12 rounded-lg border bg-white p-3 text-xs text-gray-700">
                <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
                  <div>
                    <span className="font-semibold">Product:</span>{' '}
                    {selectedProductName || '-'}
                  </div>
                  <div>
                    <span className="font-semibold">MK Barcode:</span>{' '}
                    {selectedBarcodeRow.mk_barcode || '-'}
                  </div>
                  <div>
                    <span className="font-semibold">Vendor Barcode:</span>{' '}
                    {selectedBarcodeRow.barcode || '-'}
                  </div>
                  <div>
                    <span className="font-semibold">Product Code:</span>{' '}
                    {selectedBarcodeRow.product_code || '-'}
                  </div>
                </div>
              </div>
            )}

            <div className="col-span-6 md:col-span-2">
              <label className="mb-1 block text-xs font-semibold">
                No. Units
              </label>
              <input
                type="number"
                min="0.001"
                step="any"
                value={noOfUnits}
                onChange={(e) => setNoOfUnits(e.target.value)}
                className="h-11 w-full rounded-lg border px-3 py-2 text-right"
              />
            </div>

            <div className="col-span-6 md:col-span-2">
              <label className="mb-1 block text-xs font-semibold">
                Expected Price / Unit
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={expectedUnitPrice}
                onChange={(e) => setExpectedUnitPrice(e.target.value)}
                className="h-11 w-full rounded-lg border px-3 py-2 text-right"
              />
            </div>

            <div className="col-span-12 flex items-end md:col-span-2">
              <button
                type="button"
                onClick={addItem}
                className="h-11 w-full rounded-lg bg-blue-700 px-4 py-2 font-semibold text-white hover:bg-blue-800"
              >
                Add
              </button>
            </div>
          </div>
        </section>

        {supplierId && (
          <section aria-label="Supplier purchase history" className="rounded-xl border bg-blue-50 p-4">
            <h3 className="font-semibold">Previously purchased from {supplierSearch}</h3>
            <p className="mt-1 text-xs text-gray-600">Click a product to add it with its last purchased pack count and price. Amounts are product costs before additional charges.</p>
            {purchaseHistory.length === 0 ? (
              <p className="mt-3 text-sm text-gray-600">No previous received or verified purchases for this supplier.</p>
            ) : (
              <div className="mt-3 max-h-80 overflow-auto rounded-lg border bg-white" tabIndex={0}>
                <table className="w-full min-w-[960px] text-sm">
                  <thead className="sticky top-0 bg-gray-100">
                    <tr>
                      <th className="p-3 text-left">Product</th>
                      <th className="p-3 text-left">Brand</th>
                      <th className="p-3 text-right">Pack size</th>
                      <th className="p-3 text-right">Total packs purchased</th>
                      <th className="p-3 text-right">Total quantity purchased</th>
                      <th className="p-3 text-right">Last price / pack</th>
                      <th className="p-3 text-right">Total purchased amount</th>
                      <th className="p-3 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchaseHistory.map(row => (
                      <tr key={row.key} className="cursor-pointer border-t hover:bg-blue-50" onClick={() => addHistoryItem(row)}>
                        <td className="p-3 font-medium">{row.productName}</td>
                        <td className="p-3">{row.brandName || '-'}</td>
                        <td className="p-3 text-right">{row.packQuantity.toLocaleString()} {row.unitName}</td>
                        <td className="p-3 text-right">{row.totalUnits.toLocaleString()}</td>
                        <td className="p-3 text-right">{row.totalQuantity.toLocaleString()} {row.unitName}</td>
                        <td className="p-3 text-right">₹{row.lastUnitPrice.toFixed(2)}</td>
                        <td className="p-3 text-right">₹{row.totalAmount.toFixed(2)}</td>
                        <td className="p-3 text-center">
                          <button type="button" aria-label={`Add ${row.productName} to purchase`}
                            onClick={event => { event.stopPropagation(); addHistoryItem(row); }}
                            className="whitespace-nowrap rounded-lg bg-blue-700 px-3 py-2 font-semibold text-white hover:bg-blue-800">
                            Add to purchase
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        <p className="text-xs text-gray-500">Scroll horizontally to view all purchase columns on smaller screens.</p>
        <div className="purchase-create-items-scroll" role="region" aria-label="Purchase items" tabIndex={0}>
          <table className="purchase-create-items-table border text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="w-[140px] p-2 text-left">Supplier</th>
                <th className="w-[280px] p-2 text-left">Product</th>
                <th className="w-[140px] p-2 text-left">Category</th>
                <th className="w-[140px] p-2 text-left">Brand</th>
                <th className="w-[90px] p-2 text-right">Qty</th>
                <th className="w-[100px] p-2 text-left">Unit</th>
                <th className="w-[120px] p-2 text-right">No. Units</th>
                <th className="w-[160px] p-2 text-right">
                  Expected Price / Unit
                </th>
                <th className="w-[140px] p-2 text-right">Total</th>
                <th className="w-[100px] p-2 text-center">Remove</th>
              </tr>
            </thead>

            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan="10" className="p-4 text-center text-gray-500">
                    No items added yet
                  </td>
                </tr>
              )}

              {items.map((item, index) => (
                <tr
                  key={`${item.supplier_id}-${item.product_id}-${item.product_barcode_id}-${index}`}
                  className="border-t"
                >
                  <td className="p-2">{item.supplier_name || '-'}</td>

                  <td className="p-2">
                    <div className="font-medium">{item.product_name || '-'}</div>
                    <div className="text-xs text-gray-500">
                      Code: {item.product_code || '-'}
                    </div>
                    <div className="text-xs text-gray-500">
                      MK: {item.mk_barcode || '-'}
                    </div>
                    <div className="text-xs text-gray-500">
                      Vendor: {item.barcode || '-'}
                    </div>
                  </td>

                  <td className="p-2">{item.category_name || '-'}</td>
                  <td className="p-2">{item.brand_name || '-'}</td>

                  <td className="p-2">
                    <input
                      type="text"
                      value={item.qty}
                      readOnly
                      aria-label={`Pack quantity for ${item.product_name}`}
                      className="w-full rounded border bg-gray-100 px-2 py-1 text-right"
                    />
                  </td>

                  <td className="p-2">{item.unit_name || '-'}</td>

                  <td className="p-2">
                    <input
                      type="number"
                      min="0.001"
                      step="any"
                      value={item.no_of_units}
                      aria-label={`Number of units for ${item.product_name}`}
                      onChange={(e) =>
                        updateRow(index, 'no_of_units', e.target.value)
                      }
                      className="w-full rounded border px-2 py-1 text-right"
                    />
                  </td>

                  <td className="p-2">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.expected_unit_price}
                      aria-label={`Expected price per unit for ${item.product_name}`}
                      onChange={(e) =>
                        updateRow(index, 'expected_unit_price', e.target.value)
                      }
                      className="w-full rounded border px-2 py-1 text-right"
                    />
                  </td>

                  <td className="p-2 text-right font-semibold">
                    ₹
                    {(
                      Number(item.no_of_units || 1) *
                      Number(item.expected_unit_price || 0)
                    ).toFixed(2)}
                  </td>

                  <td className="p-2 text-center">
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="text-red-600 hover:underline"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}

              {items.length > 0 && (
                <tr className="bg-gray-50 font-bold">
                  <td colSpan="8" className="p-3 text-right">
                    Grand Total
                  </td>
                  <td className="p-3 text-right">₹{grandTotal.toFixed(2)}</td>
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting}
            className="rounded-lg bg-green-700 px-5 py-2 text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            {submitting ? 'Saving...' : openingStock ? 'Save opening-stock draft' : 'Create Purchase Orders'}
          </button>
          {openingStock && <button type="button" disabled={submitting || !openingDraftSaved} onClick={() => submitHandler(null, true)} className="ml-3 rounded-lg bg-blue-700 px-5 py-2 text-white disabled:opacity-50">Publish saved opening-stock draft</button>}
        </div>
      </form>
    </section>
  );
};

export default CreatePurchaseOrderSection;
